/**
 * Typed endpoint functions for `/api/users/*` (docs/APP_PLAN.md §8.1, §12
 * M4). Covers the public profile, case-insensitive player search, and the
 * recent-opponents list — every surface is already block-filtered
 * server-side (docs/APP_PLAN.md §9.6); this client performs no filtering
 * of its own.
 */
import type { AvatarConfig } from '@perplexicon/validation';

import type { HttpClient } from './http-client.js';
import { buildQueryString } from './query.js';
import type {
  CurrentUser,
  PaginationParams,
  ProfileCustomization,
  PublicProfile,
  RecentOpponent,
  SearchResult,
} from './types.js';

interface CurrentUserEnvelope {
  user: CurrentUser;
}

interface PublicProfileEnvelope {
  profile: PublicProfile;
}

interface SearchResultsEnvelope {
  results: SearchResult[];
}

interface RecentOpponentsEnvelope {
  results: RecentOpponent[];
}

export interface UsersApi {
  /** `GET /api/users/:username`. Never includes email (docs/APP_PLAN.md §9.8). Throws `NOT_FOUND` for a nonexistent or blocked-pair username. */
  getProfile(username: string): Promise<PublicProfile>;
  /** `GET /api/users/search`. Case-insensitive prefix match on username, excluding the caller. */
  search(query: string, pagination?: PaginationParams): Promise<SearchResult[]>;
  /** `GET /api/users/recent-opponents`. Empty until the caller has played a multiplayer session (Milestone 5). */
  recentOpponents(pagination?: PaginationParams): Promise<RecentOpponent[]>;
  /** `PATCH /api/users/me/avatar`. Saves the caller's avatar configuration and returns the updated account. */
  updateAvatar(config: AvatarConfig): Promise<CurrentUser>;
  /** `PATCH /api/users/me/profile`. Saves the caller's selected profile title + cosmetic slot ids (APP_SPEC §13.2) and returns the updated account. */
  updateProfileCustomization(customization: ProfileCustomization): Promise<CurrentUser>;
}

export function createUsersApi(http: HttpClient): UsersApi {
  return {
    async getProfile(username) {
      const { profile } = await http.request<PublicProfileEnvelope>({
        method: 'GET',
        path: `/users/${encodeURIComponent(username)}`,
      });
      return profile;
    },

    async search(query, pagination = {}) {
      const { results } = await http.request<SearchResultsEnvelope>({
        method: 'GET',
        path: `/users/search${buildQueryString({ q: query, ...pagination })}`,
      });
      return results;
    },

    async recentOpponents(pagination = {}) {
      const { results } = await http.request<RecentOpponentsEnvelope>({
        method: 'GET',
        path: `/users/recent-opponents${buildQueryString({ ...pagination })}`,
      });
      return results;
    },

    async updateAvatar(config) {
      const { user } = await http.request<CurrentUserEnvelope>({
        method: 'PATCH',
        path: '/users/me/avatar',
        body: config,
      });
      return user;
    },

    async updateProfileCustomization(customization) {
      const { user } = await http.request<CurrentUserEnvelope>({
        method: 'PATCH',
        path: '/users/me/profile',
        body: customization,
      });
      return user;
    },
  };
}
